import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { $http } from "@/plugins/api";
import { getApiClientUrl } from "@/config/runtimeConfig";
import { useAuthenticationStore } from "@/store/authentication.store";
import {
  Organisation,
  Uuid,
  OrganisationsTotal,
  OrganisationConnection,
} from "@/types/organisatio";
import { decodeOrgFromToken } from "@/utils/orgToken";
import { isLegacyClientId } from "@/constants/legacyClientId";

type OrgWithAncestors = Organisation & {
  parent_uuid?: string | null;
  ancestors?: OrgWithAncestors[];
};

// The Vuex dispatch was untyped (`any`), so consumers pass partial / reactive
// form payloads and mixed-value query params. These loose types mirror that
// contract rather than the strict `Apiservice`/`FormData`/`Params`.
type OrgFormData = {
  uuid?: string;
  name?: string;
  description?: string;
  country?: string;
  customerID?: string | number;
  [key: string]: unknown;
};

type OrgMutationPayload = {
  formData: OrgFormData;
  parent_uuid?: string | null;
  organisation_type?: string;
};

type OrgListParams = Record<string, string | number | boolean>;

const normalizeOrgName = (org: OrgWithAncestors) => {
  const isRootSystem = org.organisation_type === "System" && !org.parent_uuid;
  return isRootSystem ? { ...org, name: "System" } : org;
};

// Track in-flight fetches to avoid duplicate concurrent calls (preserved from Vuex).
const inFlightOrgFetches: Record<
  string,
  Promise<OrgWithAncestors | null> | undefined
> = {};
const inFlightOrgLists: Record<string, Promise<unknown> | undefined> = {};

export const useOrganisationsStore = defineStore("organisations", () => {
  const organisations = ref<Organisation[]>([]);
  const organisationsConnections = ref<OrganisationConnection[]>([]);
  const organisationsTotal = ref<OrganisationsTotal>({ total: 0 });
  const orgsPerCustomer = ref<Organisation[]>([]);

  // Internal ancestor cache (was `orgByUuid` state + getter; the getter had no
  // external consumers, so it stays private to the store).
  const orgByUuid = ref<Record<string, OrgWithAncestors>>({});

  // Getter preserved under its original (typo'd) name for consumer parity.
  const orginisationsList = computed(() => organisations.value);

  const setOrgDetail = (org: OrgWithAncestors) => {
    orgByUuid.value = {
      ...orgByUuid.value,
      [org.uuid]: normalizeOrgName(org),
    };
  };

  const applyUpdatedOrg = (updatedOrg: Organisation) => {
    organisations.value = organisations.value.map((org) =>
      org.uuid === updatedOrg.uuid
        ? normalizeOrgName(updatedOrg as OrgWithAncestors)
        : org,
    );
  };

  const clearOrgCache = () => {
    organisations.value = [];
    organisationsConnections.value = [];
    organisationsTotal.value = { total: 0 };
    orgsPerCustomer.value = [];
    orgByUuid.value = {};
  };

  const getOrgWithAncestors = async ({ uuid }: { uuid: string }) => {
    const inflight = inFlightOrgFetches[uuid];
    if (inflight) return inflight;

    const cached = orgByUuid.value?.[uuid];
    if (cached?.ancestors && cached?.name) {
      return cached;
    }

    const fetchPromise = (async () => {
      // Walk parents until root using /v1/idp/orgs (no include=ancestors until API is ready)
      const ancestors: OrgWithAncestors[] = [];
      let org: OrgWithAncestors | null = null;
      let childOrg: OrgWithAncestors | null = null;
      let currentUuid: string | null = uuid;
      const safety = 20;
      // The signed-in org bounds the walk: never climb above it (the parent
      // is out of scope and 403s). Password/SSO logins carry it in the JWT;
      // the Auth0 session flow stores no token (opaque httpOnly cookie) and
      // only populates `selectedAccount`, so fall back to that.
      const authStore = useAuthenticationStore();
      const access = authStore.token?.access_token || null;
      const selectedAccountUuid =
        decodeOrgFromToken(access)?.uuid ||
        authStore.selectedAccount?.uuid ||
        null;
      let hops = 0;
      while (currentUuid && hops < safety) {
        const cachedParent = orgByUuid.value?.[currentUuid];
        const res = cachedParent
          ? { data: cachedParent }
          : await $http.get(`${getApiClientUrl()}/v1/idp/orgs/${currentUuid}`);
        const current = normalizeOrgName(res.data as OrgWithAncestors);
        if (!childOrg) {
          childOrg = current; // first iteration is the requested org
        } else {
          ancestors.unshift({
            uuid: current.uuid,
            name: current.name,
            organisation_type: current.organisation_type,
            parent_uuid: current.parent_uuid,
          } as OrgWithAncestors);
        }
        currentUuid = current.parent_uuid || null;
        const hitSignedIn =
          selectedAccountUuid && current.uuid === selectedAccountUuid;
        const hitRoot = !current.parent_uuid;
        if (hitSignedIn || hitRoot) {
          org = { ...childOrg, ancestors } as OrgWithAncestors;
          break;
        }
        hops += 1;
      }

      if (org) {
        setOrgDetail(org);
      }
      return org;
    })();

    inFlightOrgFetches[uuid] = fetchPromise.finally(() => {
      delete inFlightOrgFetches[uuid];
    });

    return fetchPromise;
  };

  const updateOrg = async ({
    formData,
    parent_uuid,
    organisation_type,
  }: OrgMutationPayload) => {
    const postData = {
      ...(formData.name && { name: formData.name }),
      ...(formData.country && { country: formData.country }),
      ...(parent_uuid && { parent_uuid: parent_uuid }),
      ...(formData.description && { description: formData.description }),
      ...(formData.customerID && {
        billing_system_id: Number(formData.customerID),
      }),
      ...(organisation_type && {
        organisation_type: organisation_type,
      }),
    };
    const res = await $http.patch(
      `${getApiClientUrl()}/v1/idp/orgs/${formData.uuid}`,
      postData,
    );
    applyUpdatedOrg(res.data);
    return res.data;
  };

  const showOrg = async ({ uuid }: Uuid) => {
    if (!uuid || uuid === undefined) {
      return null;
    }
    const res = await $http.get(
      `${getApiClientUrl()}/v1/idp/orgs/${uuid}?include=licences`,
    );
    applyUpdatedOrg(res.data);
    return res;
  };

  const getCustomerPerUsageOrg = async ({
    parent_uuid,
    solution_uuid,
    limit,
    filter,
    sortdir,
    offset = 0,
    sortkey = null,
  }: {
    parent_uuid?: string;
    solution_uuid?: string;
    limit?: number;
    filter?: string;
    sortdir?: string;
    offset?: number;
    sortkey?: string | null;
  }) => {
    let url = `${getApiClientUrl()}/v1/idp/orgs?include=licences`;

    if (parent_uuid) url += `&parent_uuid=${parent_uuid}`;
    if (solution_uuid) url += `&solution_uuid=${solution_uuid}`;
    if (limit) url += `&limit=${limit}`;
    if (filter) url += `&filter=${filter}`;
    if (sortkey) url += `&sortkey=${sortkey}`;
    if (offset) url += `&offset=${offset}`;
    if (sortdir) url += `&sortdir=${sortdir}`;

    const orgsResponse = await $http.get(url);

    orgsPerCustomer.value = orgsResponse.data.data;
  };

  const getOrginisationsList = async ({
    parent_uuid,
    solution_uuid,
    params,
    with_connections,
  }: {
    parent_uuid?: string | null;
    solution_uuid?: string | null;
    params: OrgListParams;
    with_connections?: boolean;
  }) => {
    const key = JSON.stringify({
      parent_uuid,
      solution_uuid,
      params,
      with_connections,
    });
    if (inFlightOrgLists[key]) {
      return inFlightOrgLists[key];
    }

    const fetchPromise = (async () => {
      const baseUrl = `${getApiClientUrl()}/v1/idp/orgs`;
      // VITE_API_CLIENT can be relative (e.g. "/api") on environments that
      // serve SPA + API from the same origin via a proxy. The second arg
      // resolves relative paths against the page origin; absolute baseUrls
      // ignore the base entirely, so it's a no-op there.
      const url = new URL(baseUrl, globalThis.location.origin);
      const reqParams = new URLSearchParams();
      reqParams.append("include", "licences");
      if (parent_uuid) {
        reqParams.append("parent_uuid", parent_uuid);
      }
      if (solution_uuid) {
        reqParams.append("solution_uuid", solution_uuid);
      }

      // Convert params to lowercase strings and append them to reqParams
      for (const [paramKey, value] of Object.entries(params)) {
        reqParams.append(paramKey, (value ?? "").toString().toLowerCase());
      }

      url.search = reqParams.toString();

      const orgsResponse = await $http.get(url.toString());
      const uuids = orgsResponse.data.data
        .map((obj: { uuid: string }) => obj.uuid)
        .join(",");

      if (with_connections && uuids) {
        const connectionsUrl = `${getApiClientUrl()}/v1/pt/organisations/connection-status?uuids=${uuids}`;
        const connectionsResponse = await $http.get(connectionsUrl);
        const connections: OrganisationConnection[] =
          connectionsResponse.data.results || [];

        organisationsConnections.value = connections.map((connection) => ({
          ...connection,
          legacy: isLegacyClientId(connection.client_id),
        }));
      }

      organisations.value = orgsResponse.data.data;
      organisationsTotal.value = { total: orgsResponse.data.total };
      return orgsResponse.data;
    })();

    inFlightOrgLists[key] = fetchPromise.finally(() => {
      delete inFlightOrgLists[key];
    });

    return fetchPromise;
  };

  // New IDP organisations "batch" delete endpoint.
  const batchDeleteOrganisations = async ({
    accounts,
  }: {
    accounts: { uuid: string }[];
  }) => {
    const deletedUuidsResponse: string[] = [];

    for (const account of accounts) {
      const response = await $http.delete(
        `${getApiClientUrl()}/v1/idp/orgs/${account.uuid}`,
        {
          _hasCustomErrorHandling: true,
        },
      );
      if (response.data && response.data.uuid) {
        deletedUuidsResponse.push(response.data.uuid);
      }
    }

    for (const id of deletedUuidsResponse) {
      organisations.value = organisations.value.filter(
        (organisation) => organisation.uuid !== id,
      );
    }

    return deletedUuidsResponse;
  };

  const createPhishTitanOrg = async (organisationUuid: string | undefined) => {
    await $http.post(`${getApiClientUrl()}/v1/pt/orgs`, {
      organisationID: organisationUuid,
      globalOrganisationID: organisationUuid,
    });
  };

  const createOrginisation = async ({
    formData,
    parent_uuid,
    organisation_type,
  }: OrgMutationPayload) => {
    const url = parent_uuid
      ? `${getApiClientUrl()}/v1/idp/orgs?parent_uuid=${parent_uuid}`
      : `${getApiClientUrl()}/v1/idp/orgs`;

    const postData: { [key: string]: unknown } = {
      ...(formData.name && { name: formData.name }),
      ...(formData.country && { country: formData.country }),
      ...(formData.description && { description: formData.description }),
      ...(formData.customerID && {
        billing_system_id: Number(formData.customerID),
      }),
      ...(organisation_type && {
        organisation_type: organisation_type,
      }),
    };
    postData.parent_uuid = parent_uuid;
    const res = await $http.post(url, postData);
    organisations.value.push(normalizeOrgName(res.data as OrgWithAncestors));
    return res;
  };

  return {
    organisations,
    orginisationsList,
    organisationsConnections,
    organisationsTotal,
    orgsPerCustomer,
    clearOrgCache,
    getOrgWithAncestors,
    updateOrg,
    showOrg,
    getCustomerPerUsageOrg,
    getOrginisationsList,
    batchDeleteOrganisations,
    createPhishTitanOrg,
    createOrginisation,
  };
});
